import { dayjs } from '@/extendedDayjs';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { colorForChecklistItemStatus } from '../../helpers';
import { displayUserNameShort, displayTextForChecklistItemStatusType } from '../../helpers/displayHelpers';
import { RootState } from '../../reducers/reducers';
import {
    ChecklistItem,
    ChecklistItemComment,
    ChecklistItemStatusType,
    DefaultUserGroupId,
    IntegrationCapability,
    OrgUser,
    RelativeTimeTrigger,
    UserGroup,
    UserGroupId,
} from '../../types';
import { antGrey, getAvatarColorForName, tagStyle } from './../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import Avatar from 'antd/lib/avatar/avatar';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import Paragraph from 'antd/lib/typography/Paragraph';
import { getLocaleDateTimeFormat } from '../../timeHelpers';
import { MessageOutlined, MessageFilled } from '@ant-design/icons';
import Select from 'antd/lib/select';
import SmallStatusNode from '../SmallStatusNode';
import Popover from 'antd/lib/popover';
import MarkdownComponent from '../HOCs/Markdown';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import {
    selectActivePilotForRelease,
    selectCLIUsers,
    selectUserById,
    selectAppHasSelectCapabilitiesConfigured,
    selectAppTimeFormatSetting,
} from '../../selectors/selectors';
import Tag from 'antd/lib/tag';
import { OwnerAvatar, OwnerAvatarGroup, ReleasePilotAvatar } from '../Avatars';
import PingButton from '../PingButton';
import GroupTooltip from './GroupTooltip';
import { TargetCompletionDueView } from '@/components/ChecklistItemTargetTime';
import Divider from 'antd/lib/divider';
import { orgLevelCLIIcon } from '../ChecklistItemRowComponent';

const PINGABLE_STATUSES = [
    ChecklistItemStatusType.InProgress,
    ChecklistItemStatusType.Blocked,
    ChecklistItemStatusType.Failed,
    ChecklistItemStatusType.Rejected,
];

interface ExpandedChecklistItemRowComponentProps extends ChecklistItem, ReleaseComponentProps {
    isEditing: boolean;
    showStatus?: ChecklistItemStatusType;
    showAsComplete: boolean;
    onClickInfo: (id: string, title: string, description?: string) => void;
    onChangeStatus: (status: ChecklistItemStatusType, id: string) => void;
    onAddComment: (id: string) => void;
    isDisabled: boolean;
    isLoading: boolean;
    userGroups: Record<UserGroupId, UserGroup>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    dueDate: Nullable<string>;
    targetCompletion?: RelativeTimeTrigger;
}

const ExpandedChecklistItemRowComponent: React.FC<ExpandedChecklistItemRowComponentProps> = (
    props: ExpandedChecklistItemRowComponentProps,
) => {
    const { orgId, appId, releaseId, isEditing, showAsComplete, onClickInfo, isDisabled } = props;
    const selectRef = React.useRef(null);

    const cliOwnerUsers = useSelector((state: RootState) => selectCLIUsers(state, orgId, appId, props.owners));
    const hasCommsIntegrationConnected = useSelector((state: RootState) =>
        selectAppHasSelectCapabilitiesConfigured(state, appId, [IntegrationCapability.Notifications]),
    );

    const hasIndividualOwners = props.owners?.length > 0;
    const hasOneOwner = props.owners?.length === 1;
    const hasMultipleOwners = props.owners?.length > 1;

    const activeReleasePilotUser = useSelector((state: RootState) =>
        selectActivePilotForRelease(state, orgId, releaseId),
    );

    const ownedByPilot =
        hasOneOwner &&
        activeReleasePilotUser?.id === props.owners[0] &&
        props.approverGroups.length === 1 &&
        props.approverGroups[0] === DefaultUserGroupId.Pilot;

    const approvedByUser = useSelector((state: RootState) => {
        if (props.status?.createdBy) {
            return selectUserById(state, orgId, appId, props.status.createdBy);
        }

        return null;
    });

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));

    const avatar = (displayName: string) => {
        return (
            <Avatar
                size="small"
                src={approvedByUser.commsAvatars?.image32}
                style={{
                    backgroundColor: getAvatarColorForName(displayName),
                    opacity: opacity,
                }}
            >
                {displayName.charAt(0).toUpperCase()}
            </Avatar>
        );
    };

    const approvedByUserAvatar = (user: OrgUser) => {
        const displayName = displayUserNameShort(user);
        return isDisabled ?
                avatar(displayName)
            :   <Tooltip title={displayName} placement="topRight">
                    {avatar(displayName)}
                </Tooltip>;
    };

    const textColor = isDisabled ? antGrey.grey6 : antGrey.grey8;
    const opacity = isDisabled ? '0.3' : '1.0';

    const renderComment = (comment?: ChecklistItemComment) => {
        if (comment) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <MarkdownComponent source={comment.comment} />
                    <Text type="secondary" style={{ marginTop: 12, color: textColor }}>
                        Last updated by {comment.createdBy} on{' '}
                        {dayjs(comment.createdAt).format(getLocaleDateTimeFormat('short', use24HourTimeFormat))}
                    </Text>
                </div>
            );
        }

        return null;
    };

    const commentButton = (
        <Button
            icon={<MessageFilled style={{ color: antGrey.grey7 }} />}
            style={{ borderStyle: 'none', display: 'block' }}
            onClick={() => props.onAddComment(props.id)}
            disabled={isDisabled}
        />
    );

    const hasApproverGroupAndNoIndividualOwners =
        props.approverGroups && props.approverGroups.length > 0 && !hasIndividualOwners;
    const hasIndividualOwnerAvailable = hasIndividualOwners && cliOwnerUsers[0];
    const disablePing =
        (props.status && !PINGABLE_STATUSES.includes(props.status.status)) || !hasCommsIntegrationConnected;
    const itemInProgressOrComplete = !!props.status || props.showAsComplete;

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            <Space size={16}>
                <Space>
                    <SmallStatusNode color={colorForChecklistItemStatus(props.showStatus)} />
                    <Paragraph
                        style={
                            showAsComplete ?
                                {
                                    marginBottom: 0,
                                    flexShrink: 1,
                                    textDecoration: 'line-through',
                                    color: isDisabled ? antGrey.grey5 : antGrey.grey7,
                                }
                            :   { marginBottom: 0, flexShrink: 1, color: textColor }
                        }
                    >
                        {!!props.orgTemplateId && orgLevelCLIIcon(props.orgId)}
                        {props.title}
                    </Paragraph>
                    {props.description && (
                        <Button
                            onClick={() => onClickInfo(props.id, props.title, props.description)}
                            size="small"
                            shape="round"
                            disabled={isDisabled}
                        >
                            Info
                        </Button>
                    )}
                </Space>
                {props.oneOffForVersion && (
                    <Tag
                        color={isDisabled ? antGrey.grey4 : antGrey.grey6}
                        style={{ ...tagStyle, lineHeight: '10px', padding: '2px 4px' }}
                    >
                        {props.oneOffForVersion}
                    </Tag>
                )}
            </Space>
            <Space direction="horizontal" size="middle">
                {props?.targetCompletion?.enabled && !props.showAsComplete && !!props.dueDate && (
                    <div style={{ display: 'flex', columnGap: 16 }}>
                        <TargetCompletionDueView isDisabled={isDisabled} dueDate={props.dueDate} />
                        <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch', margin: 0 }} />
                    </div>
                )}
                <Space size="middle" style={{ display: 'flex' }}>
                    {approvedByUser && approvedByUserAvatar(approvedByUser)}
                    {props.status && (
                        <Text type="secondary" style={{ marginRight: 8, opacity: opacity }}>
                            {dayjs(props.status.createdAt).format(
                                getLocaleDateTimeFormat('short', use24HourTimeFormat),
                            )}
                        </Text>
                    )}
                    {!itemInProgressOrComplete && hasApproverGroupAndNoIndividualOwners && (
                        <>
                            <Text
                                type="secondary"
                                style={{
                                    fontSize: 14,
                                    opacity: opacity,
                                }}
                            >
                                owned by
                            </Text>
                            {props.approverGroups.map((userGroupId, index) => {
                                const omitComma = index === props.approverGroups.length - 1;
                                return (
                                    <GroupTooltip
                                        key={index}
                                        orgId={props.orgId}
                                        appId={props.appId}
                                        releaseId={props.releaseId}
                                        group={props.userGroups[userGroupId]}
                                        showComma={!omitComma}
                                        isDisabled={isDisabled}
                                    />
                                );
                            })}
                        </>
                    )}

                    {!itemInProgressOrComplete && hasIndividualOwnerAvailable && (
                        <>
                            <Text
                                type="secondary"
                                style={{
                                    fontSize: 14,
                                    opacity: opacity,
                                }}
                            >
                                owned by
                            </Text>
                            {hasOneOwner && (
                                <Space size="small" style={{ display: 'flex' }}>
                                    <OwnerAvatar user={cliOwnerUsers[0]} isDisabled={isDisabled} />
                                    <Text style={{ color: textColor }}>{displayUserNameShort(cliOwnerUsers[0])}</Text>
                                    {ownedByPilot && <ReleasePilotAvatar isDisabled={isDisabled} />}
                                </Space>
                            )}
                            {hasMultipleOwners && (
                                <OwnerAvatarGroup maxAvatars={3} owners={cliOwnerUsers} isDisabled={isDisabled} />
                            )}
                        </>
                    )}
                    <Select
                        placeholder="Not started"
                        onChange={(value) => {
                            props.onChangeStatus(value as ChecklistItemStatusType, props.id);

                            selectRef.current.blur();
                        }}
                        value={props.showStatus}
                        style={{ width: 180 }}
                        ref={selectRef}
                        disabled={isDisabled}
                        loading={props.isLoading}
                    >
                        <Select.Option key={0} value={null} style={{ color: antGrey.grey7 }}>
                            Not started
                        </Select.Option>
                        {props.statuses.map((status, index) => {
                            return (
                                <Select.Option key={index + 1} value={status}>
                                    {displayTextForChecklistItemStatusType(status)}
                                </Select.Option>
                            );
                        })}
                    </Select>
                    <div>
                        {props.comment &&
                            (isDisabled ?
                                <>{commentButton}</>
                            :   <Popover
                                    title={null}
                                    content={renderComment(props.comment)}
                                    placement="topRight"
                                    mouseEnterDelay={0.4}
                                >
                                    <>{commentButton}</>
                                </Popover>)}
                        {!props.comment && (
                            <Button
                                icon={<MessageOutlined style={{ color: antGrey.grey7 }} />}
                                style={{ borderStyle: 'none', display: 'block' }}
                                onClick={() => props.onAddComment(props.id)}
                                disabled={isDisabled}
                            />
                        )}
                    </div>
                    {!isEditing && (
                        <div>
                            <PingButton
                                orgId={props.orgId}
                                appId={props.appId}
                                releaseId={props.releaseId}
                                checklistItemId={props.id}
                                text={`${hasMultipleOwners ? 'Ping all' : 'Ping'}`}
                                disabled={disablePing}
                                disablePopover={isDisabled}
                                missingIntegration={!hasCommsIntegrationConnected}
                                stepRunComponentProps={props.stepRunComponentProps}
                            />
                        </div>
                    )}
                </Space>
            </Space>
        </div>
    );
};

export default ExpandedChecklistItemRowComponent;
